import { useEffect, useState } from "react";

import { resolveAssignedPricingLines } from "@/lib/bookingCommercial";
import { apiRateCardToRow } from "@/lib/mapRate";
import { api } from "@/services/api";
import type { ApiBooking, BookingPricingLine } from "@/types/api";
import type { RateCardRow } from "@/types/rate";

export function useBookingAssignedPricing(
  sourceId: string | null,
  booking: Pick<
    ApiBooking,
    "all_contract_refs" | "contract_ref" | "all_carriers" | "carrier"
  > | null,
  contractRef?: string | null,
  /**
   * When `true`, skip the `/pricing` fetch entirely. Used for consignee-invoice
   * / hidden tier viewers who must never see the internal rate-line breakdown
   * (the backend also returns an empty set for them).
   */
  skipFetch = false,
) {
  const [lines, setLines] = useState<BookingPricingLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sourceId || !booking || skipFetch) {
      setLines([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getBookingPricing(sourceId)
      .then((rows) => {
        if (cancelled) return;
        setLines(resolveAssignedPricingLines(booking, rows, contractRef));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load booking pricing", err);
        setLines([]);
        setError(
          err instanceof Error
            ? err.message
            : "Could not load rate details. Please try again.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sourceId, booking, contractRef, skipFetch]);

  return { lines, loading, error };
}

export function pricingValidityLabel(
  lines: BookingPricingLine[],
  formatDate: (iso?: string | null) => string | undefined,
): string | undefined {
  const from = lines.find((l) => l.rate_valid_from)?.rate_valid_from;
  const to = lines.find((l) => l.rate_valid_to)?.rate_valid_to;
  return contractValidityLabel(from, to, formatDate);
}

const MISSING_DATE_LABEL = "—";

function formattedDateLabel(
  iso: string | null | undefined,
  formatDate: (iso?: string | null) => string | undefined,
): string | undefined {
  if (!iso?.trim()) return undefined;
  const label = formatDate(iso);
  if (!label || label === MISSING_DATE_LABEL) return undefined;
  return label;
}

export function contractValidityLabel(
  validFrom?: string | null,
  validTo?: string | null,
  formatDate: (iso?: string | null) => string | undefined = (iso) =>
    iso ?? undefined,
): string | undefined {
  const fromLabel = formattedDateLabel(validFrom, formatDate);
  const toLabel = formattedDateLabel(validTo, formatDate);
  if (fromLabel && toLabel) return `${fromLabel} – ${toLabel}`;
  if (fromLabel) return `From ${fromLabel}`;
  if (toLabel) return `Until ${toLabel}`;
  return undefined;
}

/** Load the rate card backing this booking's contract (id first, then list lookup). */
export function useBookingContractCard(
  rateCardId?: string | null,
  contractRef?: string | null,
  carrier?: string | null,
) {
  const [card, setCard] = useState<RateCardRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const id = rateCardId?.trim();
    const ref = contractRef?.trim();
    if (!id && !ref) {
      setCard(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        if (id) {
          const detail = await api.getRateCard(id);
          if (!cancelled) setCard(apiRateCardToRow(detail));
          return;
        }
        const response = await api.getRateCards({
          contract_ref: ref,
          carrier: carrier?.trim() || undefined,
          per_page: 5,
          sort_by: "valid_from",
          sort_dir: "desc",
        });
        if (cancelled) return;
        const match =
          response.data.find(
            (row) => row.contract_ref?.trim() === ref,
          ) ?? response.data[0];
        setCard(match ? apiRateCardToRow(match) : null);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load contract rate card", err);
        setCard(null);
        setError(
          err instanceof Error
            ? err.message
            : "Could not load contract details. Please try again.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [rateCardId, contractRef, carrier]);

  return { card, loading, error };
}
